<script setup lang="ts">
// components
import SvgIcon from '~/components/SvgIcon.vue';
import Modals from '~/components/redesign/Modals';
import Forms from '~/components/redesign/Forms';
import Buttons from '~/components/redesign/Buttons';

// composables
import useApiSeo from '~/composables/useApiSeo';

// services
import { Cookie, JWT } from '~/services';

// stores
import useUIStore from '~/stores/ui';

// utils
import { isString, isEmpty } from '~/utils';

// constants
import { LINKS, API_ENDPOINTS, COOKIE_KEYS } from '~/constants';

const dayjs = useDayjs();
const { query } = useRoute();
const { setLoading } = useUIStore();
const {
  public: { api_sizer, api_app },
} = useRuntimeConfig();

type CalculatorTypeKeys = keyof typeof LINKS.CALCULATOR;
type Environments = keyof (typeof LINKS.CALCULATOR)[CalculatorTypeKeys];
type ResponseToken = { token: string };

const env = useState<Environments>('env', () => {
  const config = useRuntimeConfig();

  return config.public.env === 'production' ? 'PROD' : 'DEV';
});

const { data: token } = useFetch(API_ENDPOINTS.AUTH_TOKEN, {
  baseURL: api_sizer,
  method: 'POST',
  server: false,
  transform: (response: ResponseToken) => response.token,
});

const linkVM = computed(() => {
  if (!isString(token.value)) return;

  const link = new URL(LINKS.CALCULATOR.VM[env.value]);
  link.searchParams.append('token', token.value);

  return link.href;
});
const linkNAS = computed(() => {
  if (!isString(token.value)) return;

  const link = new URL(LINKS.CALCULATOR.NAS[env.value]);
  link.searchParams.append('token', token.value);

  return link.href;
});

const isFormSubmitted = ref(false);
const setFormSubmitted = (value: boolean) => {
  isFormSubmitted.value = value;
};

enum ACTION {
  VM_JOB = 'VM Job',
  UNSTRUCTURED = 'Unstructured',
}

const trackClick = (action: ACTION) => {
  const {
    referrer,
    cookie,
    location: { href },
  } = document;

  $fetch(API_ENDPOINTS.SIZER_ACTION, {
    baseURL: api_app,
    method: 'POST',
    keepalive: true,
    ignoreResponseError: true,
    body: {
      action,
      href,
      referrer,
      cookies: cookie,
    },
  });
};

onMounted(async () => {
  if (isEmpty(query)) return;

  Cookie.set(COOKIE_KEYS.QUERY_PARAMS, await JWT.encode(query), {
    expires: dayjs().add(1, 'year').toDate(),
  });
});

useApiSeo('backup-storage-calculator');
</script>

<template>
  <div data-page-name="backup-storage-calculator">
    <NuxtImg
      src="/redesign/images/figures/cylinder_purple.webp"
      alt="3d purple cylinder"
      width="931"
      height="931"
      densities="x1 x2"
      format="webp"
    />

    <div class="wrapper">
      <section class="hero">
        <div class="intro">
          <h1>{{ $t('Backup_Storage_Calculator') }}</h1>
          <strong>{{
            $t('backup-storage-calculator.hero.intro.strong')
          }}</strong>
        </div>

        <ul class="cards">
          <li class="card">
            <h3>{{ $t('backup-storage-calculator.hero.cards.card-1.h3') }}</h3>
            <p>{{ $t('backup-storage-calculator.hero.cards.card-1.p') }}</p>

            <Buttons.Link
              :to="linkVM"
              external
              target="_blank"
              @click="trackClick(ACTION.VM_JOB)"
            >
              {{ $t('backup-storage-calculator.hero.cards.card-1.link') }}
              <SvgIcon name="arrow-forward" />
            </Buttons.Link>
          </li>

          <li class="card">
            <h3>{{ $t('backup-storage-calculator.hero.cards.card-2.h3') }}</h3>
            <p>{{ $t('backup-storage-calculator.hero.cards.card-2.p') }}</p>

            <Buttons.Link
              :to="linkNAS"
              external
              target="_blank"
              @click="trackClick(ACTION.UNSTRUCTURED)"
            >
              {{ $t('backup-storage-calculator.hero.cards.card-2.link') }}
              <SvgIcon name="arrow-forward" />
            </Buttons.Link>
          </li>
        </ul>

        <p class="footnote">
          {{ $t('backup-storage-calculator.hero.footnote') }}
        </p>
      </section>

      <section class="benefits">
        <h2 v-html="$t('backup-storage-calculator.benefits.h2')" />

        <ul class="cards">
          <li class="card">
            <h4>
              {{ $t('backup-storage-calculator.benefits.cards.card-1.h4') }}
            </h4>
            <SvgIcon name="darts-colorfull" />
            <p>{{ $t('backup-storage-calculator.benefits.cards.card-1.p') }}</p>
          </li>

          <li class="card">
            <h4>
              {{ $t('backup-storage-calculator.benefits.cards.card-2.h4') }}
            </h4>
            <SvgIcon name="chart-colorfull" />
            <p>{{ $t('backup-storage-calculator.benefits.cards.card-2.p') }}</p>
          </li>

          <li class="card">
            <h4>
              {{ $t('backup-storage-calculator.benefits.cards.card-3.h4') }}
            </h4>
            <SvgIcon name="heart-colorfull" />
            <p>{{ $t('backup-storage-calculator.benefits.cards.card-3.p') }}</p>
          </li>
        </ul>
      </section>

      <section class="free-consultation">
        <div class="left">
          <SvgIcon name="headset" />

          <h2>
            {{ $t('backup-storage-calculator.free-consultation.left.h2') }}
          </h2>
          <p>{{ $t('backup-storage-calculator.free-consultation.left.p') }}</p>
        </div>

        <div class="right">
          <transition name="fade" mode="out-in">
            <Forms.Wrapper v-if="isFormSubmitted">
              <Modals.ThankYou
                :title="$t('partners.become-partner.right.thank-you.title')"
                :description="
                  $t('partners.become-partner.right.thank-you.description')
                "
              />
            </Forms.Wrapper>

            <Forms.BecomePartner
              v-else
              :btn-text="$t('Contact_Me')"
              page="backup-storage-calculator"
              product="Consultation Request"
              gtm-event="consultation"
              @loading="setLoading"
              @submitted="setFormSubmitted(true)"
            />
          </transition>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '_/mixins/position';
@use '_/mixins/size';
@use '_/mixins/flex';
@use '_/mixins/typo';
@use '_/mixins/media';
@use '_/variables/shadow';
@use '_/fn';

[data-page-name='backup-storage-calculator'] {
  position: relative;
  overflow-x: hidden;

  padding: 3.91rem 0 8rem 0;

  @include media.tablet {
    padding: 5rem 0;
  }

  @include media.mobile {
    padding: 2.5rem 0;
  }

  img {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translate(11.65rem, -10.13rem);

    pointer-events: none;

    @include media.tablet {
      display: none;
    }
  }

  .wrapper {
    padding: 0 2rem;
    @include size.wrapper(80rem);

    @include media.mobile {
      padding: 0 1rem;
    }

    section.hero {
      margin-bottom: 11.91rem;

      @include media.tablet {
        margin-bottom: 10rem;
      }

      @include media.mobile {
        margin-bottom: 5rem;
      }

      .intro {
        @include size.fluid-max-width(50rem);
        margin-bottom: 2rem;

        h1 {
          @include typo.h3;
          margin-bottom: 1rem;

          @include media.mobile {
            margin-bottom: 0.5rem;
          }
        }

        strong {
          display: block;
          @include typo.subtitle-1;
        }
      }

      ul.cards {
        @include flex.stretch-start;
        gap: 0 1.5rem;
        @include size.fluid-max-width(59.5rem);
        margin-bottom: 1rem;

        @include media.mobile {
          gap: 1.5rem 0;
          flex-direction: column;
        }

        li.card {
          width: 100%;
          @include flex.start-start;
          flex-direction: column;
          padding: 2rem;
          box-shadow: shadow.$elevation-3;

          @include media.mobile {
            padding: 1.5rem;
          }

          h3 {
            @include typo.h4;
            margin-bottom: 0.5rem;
          }

          p {
            @include typo.body-2;
            margin-bottom: 2rem;

            @include media.mobile {
              @include typo.body-3;
              margin-bottom: 1.5rem;
            }
          }

          [data-component-name='Buttons.Link'] {
            margin-top: auto;
            align-self: stretch;
          }
        }
      }

      p.footnote {
        color: fn.token('text-secondary');
        @include typo.body-3;
        @include size.fluid-max-width(50rem);

        @include media.mobile {
          @include typo.body-4;
        }
      }
    }

    section.benefits {
      margin-bottom: 10rem;

      @include media.mobile {
        margin-bottom: 5.16rem;
      }

      &:deep(h2) {
        @include typo.h3;
        text-align: center;
        margin-bottom: 5rem;

        br {
          &.mobile {
            display: none;

            @include media.mobile {
              display: initial;
            }
          }
        }

        @include media.tablet {
          margin-bottom: 2.5rem;
        }
      }

      ul.cards {
        @include flex.stretch-sb;
        gap: 0 2rem;

        @include media.tablet {
          gap: 1.5rem 0;
          flex-direction: column;
        }

        li.card {
          padding: 2rem;
          width: 100%;

          @include flex.start-start;
          flex-direction: column;

          box-shadow: shadow.$elevation-1;

          @include media.tablet {
            padding: 1.5rem;
          }

          @include media.mobile {
            padding: 1.5rem 1rem;
          }

          h4 {
            @include typo.subtitle-1;
            min-height: 4.5rem;

            @include media.tablet {
              min-height: auto;
            }
          }

          svg {
            margin: 1.5rem 0;
            @include size.fixed(3.125rem);
          }

          p {
            @include typo.body-2;
          }
        }
      }
    }

    section.free-consultation {
      @include flex.start-sb;
      flex-direction: row;
      gap: 0 2rem;

      @include media.tablet {
        flex-direction: column;
        gap: 2rem 0;
      }

      @include media.mobile {
        gap: 1.5rem 0;
      }

      .left {
        svg {
          padding: 0.2rem;
          @include size.fixed(5.625rem);
          margin-bottom: 1.8rem;

          @include media.tablet {
            @include size.fixed(5rem);
            margin-bottom: 1.3rem;
          }

          @include media.mobile {
            padding: 0.1rem;
            @include size.fixed(3rem);
            margin-bottom: 0.8rem;
          }
        }

        h2 {
          @include typo.h3;
          margin-bottom: 1rem;

          @include media.tablet {
            margin-bottom: 0.5rem;
          }
        }

        p {
          @include typo.subtitle-1;
        }
      }

      .right {
        position: relative;
        @include size.fixed-width(37rem);

        @include media.tablet {
          @include size.wrapper(37rem);
        }

        @include media.mobile {
          @include size.fixed-width(100%);
        }

        [data-component-name='Forms.Wrapper'] {
          @include flex.center-center;
          min-height: 57.875rem;
        }
      }
    }
  }
}
</style>
